import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { appointmentRejectionSchema } from '../../constants';
import {
  useRejectAppointment,
  useCreateAppointmentNote,
  useUpdateAppointmentNote,
} from '../../hooks';
import Modal from 'react-modal';
import { showError, showSuccess } from '../../utils';
import { RescheduleAppointmentForm } from '../../components/templates';
import { ColoredSlug } from '../../components/atoms';
import { CreateAppointmentNote } from './create-appointment-note-from';
import { UpdateAppointmentNote } from './update-appointment-note-form';

const formatDate = (dateString) => {
  const date = new Date(dateString);

  const formattedDate = `${date.toLocaleDateString('en-US', {
    month: 'long',
  })} ${date.getDate()}, ${date.getFullYear()}`;

  return formattedDate;
};

const formatPhoneNumber = (phoneNumber) => {
  if (phoneNumber) {
    const match = phoneNumber.match(/^\+(\d)(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phoneNumber;
  }
};

const formatToHoursMin = (value) => {
  const minutes = value % 60;
  const hours = Math.floor(value / 60);
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes} min`;
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
  });
  return formattedTime;
};

const AppointmentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const handleRejectAppointment = useRejectAppointment();
  const handleCreateAppointmentNote = useCreateAppointmentNote();
  const handleUpdateAppointmentNote = useUpdateAppointmentNote();
  const [RescheduleModal, setRescheduleModal] = useState(false);
  const [selectedAppointment, setselectedAppointment] = useState(null);
  const currentTime = new Date();
  const startTime = new Date(appointmentDetails?.startTime);
  const timeDifferenceInHours = (startTime - currentTime) / (1000 * 60 * 60);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(appointmentRejectionSchema),
  });

  const [isOpen, setIsOpen] = useState(false);

  const onSubmit = async (data) => {
    const payload = {
      appointmentUid: selectedAppointment?.uid,
      ...data,
    };
    const response = await handleRejectAppointment(payload);
    if (response?.data?.rejectAppointment) {
      navigate(location.pathname, {
        replace: true,
        state: response?.data?.rejectAppointment,
      });
      showSuccess('Appointment canceled successfully');
      closeModal();
    } else {
      showError(response);
    }
  };

  const openModal = (value) => {
    reset();
    setselectedAppointment(value);
    setIsOpen(true);
  };
  const closeModal = () => {
    reset();
    setselectedAppointment(null);
    setIsOpen(false);
  };

  const openRescheduleModal = (value) => {
    setselectedAppointment(value);
    setRescheduleModal(true);
  };
  const closeRescheduleModal = () => {
    setselectedAppointment(null);
    setRescheduleModal(false);
  };

  useEffect(() => {
    if (location?.state) {
      setAppointmentDetails(location?.state);
    }
  }, [location]);

  return (
    <div>
      <section className="flex flex-col relative  bg-gray-verylight md:h-screen h-svh md:pt-0 md:bg-gray-main px-3 md:px-0 overflow-y-auto">
        <div className="pl-4 pr-4 bg-gray-verylight md:bg-gray-main md:pl-8 md:pr-8">
          <div className="pb-3 pt-3  w-full md:pb-5 md:pt-5 lg:pb-7 lg:pt-8 relative gap-x-2">
            <div className="flex justify-between items-center">
              <div>
                <div className="hidden md:block">
                  <span className="text-xs text-gray-middle">Appointments</span>
                  <span className="text-xs text-gray-middle mx-1"> &gt;</span>
                  <span className="text-xs text-gray-dark">
                    Appointment Details
                  </span>
                </div>
                <h1 className="font-bold text-lg text-black text-left md:text-xl lg:text-2xl truncate">
                  Appointment Details
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div className="border-b border-gray-light  md:border-b-2 md:border-gray-border"></div>
        <section className="py-2   md:py-5 md:flex-nowrap pl-2 pr-2 bg-gray-verylight md:bg-gray-main md:pl-8 md:pr-8">
          {appointmentDetails?.status !== 'rejected' &&
            appointmentDetails?.status !== 'completed' &&
            appointmentDetails?.status !== 'canceled' && (
              <div className="flex gap-4  justify-end">
                {timeDifferenceInHours > 0 && (
                  <button
                    className=" bg-white-main py-2 px-5 rounded text-primary font-semibold text-xs"
                    onClick={() => openRescheduleModal(appointmentDetails)}
                  >
                    Reschedule
                  </button>
                )}
                <button
                  className=" bg-white-main py-2 px-5 rounded text-red-cover font-semibold text-xs"
                  onClick={() => openModal(appointmentDetails)}
                >
                  Cancel
                </button>
              </div>
            )}

          {appointmentDetails?.comment && (
            <div className=" rounded-md border border-red-drk py-2 px-4 flex justify-center items-center bg-red-Verylight mt-4">
              <p className=" text-red-drk  font-semibold text-sm">
                {appointmentDetails?.comment}
              </p>
            </div>
          )}
          <section className="mt-6 grid md:grid-cols-2 grid-cols-1 gap-6">
            <div className="text-gray-dark cursor-pointer  hover:bg-gray-lightHover bg-white-main shadow-items gap-4 p-4 rounded bottom-1 border-gray-light w-full mb-4">
              <div className="mb-7">
                <p className="text-xs font-medium text-black-secondary">
                  Service Name: {appointmentDetails?.sellerService?.name}
                </p>
                <div className="text-right">
                  {appointmentDetails?.status === 'accepted' ? (
                    <div className="inline-flex rounded-full items-center py-0.5 whitespace-nowrap bg-green-lame px-3">
                      <span className="text-xs capitalize text-green-darklime font-medium">
                        {appointmentDetails?.status}
                      </span>
                    </div>
                  ) : appointmentDetails?.status === 'completed' ? (
                    <div className="inline-flex rounded-full items-center py-0.5 whitespace-nowrap bg-gray-completed  px-3">
                      <span className="text-xs capitalize text-gray-middle font-medium">
                        {appointmentDetails?.status}
                      </span>
                    </div>
                  ) : appointmentDetails?.status === 'rejected' ||
                    appointmentDetails?.status === 'canceled' ? (
                    <div className="inline-flex rounded-full items-center py-0.5 whitespace-nowrap bg-red-Verylight px-3">
                      <span className="text-xs capitalize text-red-drk font-medium">
                        canceled
                      </span>
                    </div>
                  ) : (
                    <div className="inline-flex rounded-full items-center py-0.5 whitespace-nowrap bg-blue-chipBg px-3">
                      <span className="text-xs capitalize text-primary font-medium">
                        Rescheduled
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid gap-4 grid-cols-3">
                <div>
                  <p className="text-xs font-medium text-black-secondary">
                    Date
                  </p>
                  <p className="font-semibold text-xs text-black-secondary">
                    {formatDate(appointmentDetails?.startTime)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-black-secondary">
                    Time
                  </p>
                  <p className="font-semibold text-xs text-black-secondary">
                    {formatTime(appointmentDetails?.startTime)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-black-secondary">
                    Duration
                  </p>
                  <p className="font-semibold text-xs text-black-secondary">
                    {formatToHoursMin(
                      appointmentDetails?.sellerService?.durationInMinutes
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="text-gray-dark cursor-pointer  hover:bg-gray-lightHover bg-white-main shadow-items gap-4 p-4 rounded bottom-1 border-gray-light w-full mb-4">
              <h2 className="text-xs font-semibold text-black-secondary">
                Customer Details
              </h2>
              <div className="mt-4">
                <div className="flex items-center justify-start gap-x-2 md:gap-x-3">
                  <div className="h-12 w-12 p-1 rounded-lg">
                    <div className="aspect-w-1 aspect-h-1 rounded-lg">
                      <ColoredSlug
                        name={
                          appointmentDetails?.familyMember?.formalName ||
                          appointmentDetails?.buyer?.formalName
                        }
                      />
                    </div>
                  </div>
                  <span className="font-semibold text-black-secondary text-xs capitalize">
                    {appointmentDetails?.familyMember?.formalName ||
                      appointmentDetails?.buyer?.formalName}
                  </span>
                </div>
              </div>
              <div className="flex gap-4  mt-4 md:flex-row flex-col flex-wrap">
                {/* <div>
                  <p className="text-xs font-medium text-black-secondary">
                    Email
                  </p>
                  <p className="font-semibold text-xs text-black-secondary break-all">
                    {appointmentDetails?.buyer?.email}
                  </p>
                </div> */}
                <div>
                  <p className="text-xs font-medium text-black-secondary">
                    Phone
                  </p>
                  <p className="font-semibold text-xs text-black-secondary">
                    {formatPhoneNumber(
                      appointmentDetails?.familyMember?.phone ||
                        appointmentDetails?.buyer?.phone
                    )}
                  </p>
                </div>
              </div>
            </div>
            {appointmentDetails?.buyerNote?.content && (
              <div className="text-gray-dark cursor-pointer  hover:bg-gray-lightHover bg-white-main shadow-items gap-4 p-4 rounded bottom-1 border-gray-light w-full mb-4">
                <h2 className="text-xs font-semibold text-black-secondary pb-3">
                  Notes
                </h2>
                <div className="border-b border-gray-light md:border-gray-border"></div>
                <div className="border-b border-gray-light py-2 my-3">
                  <p className="font-normal text-xs  text-gray-middle flex gap-3">
                    Buyer Notes -
                    <span className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="11"
                        viewBox="0 0 10 11"
                        fill="none"
                      >
                        <path
                          d="M7.5 1.49976H9.5C9.77615 1.49976 10 1.72362 10 1.99976V9.99976C10 10.2759 9.77615 10.4998 9.5 10.4998H0.5C0.22386 10.4998 0 10.2759 0 9.99976V1.99976C0 1.72362 0.22386 1.49976 0.5 1.49976H2.5V0.499756H3.5V1.49976H6.5V0.499756H7.5V1.49976ZM1 4.49976V9.49976H9V4.49976H1ZM2 5.49976H3V6.49976H2V5.49976ZM4.5 5.49976H5.5V6.49976H4.5V5.49976ZM7 5.49976H8V6.49976H7V5.49976Z"
                          fill="#8F8F8F"
                        ></path>
                      </svg>
                      {formatDate(appointmentDetails?.buyerNote?.createdAt)}
                    </span>
                    <sapn className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <path
                          d="M6.65 7.35024L7.35 6.65024L5.5 4.80024V2.50024H4.5V5.20024L6.65 7.35024ZM5 10.0002C4.30833 10.0002 3.65833 9.86899 3.05 9.60649C2.44167 9.34399 1.9125 8.98774 1.4625 8.53774C1.0125 8.08774 0.65625 7.55858 0.39375 6.95024C0.13125 6.34191 0 5.69191 0 5.00024C0 4.30858 0.13125 3.65858 0.39375 3.05024C0.65625 2.44191 1.0125 1.91274 1.4625 1.46274C1.9125 1.01274 2.44167 0.656494 3.05 0.393994C3.65833 0.131494 4.30833 0.000244141 5 0.000244141C5.69167 0.000244141 6.34167 0.131494 6.95 0.393994C7.55833 0.656494 8.0875 1.01274 8.5375 1.46274C8.9875 1.91274 9.34375 2.44191 9.60625 3.05024C9.86875 3.65858 10 4.30858 10 5.00024C10 5.69191 9.86875 6.34191 9.60625 6.95024C9.34375 7.55858 8.9875 8.08774 8.5375 8.53774C8.0875 8.98774 7.55833 9.34399 6.95 9.60649C6.34167 9.86899 5.69167 10.0002 5 10.0002ZM5 9.00024C6.10833 9.00024 7.05208 8.61066 7.83125 7.83149C8.61042 7.05233 9 6.10858 9 5.00024C9 3.89191 8.61042 2.94816 7.83125 2.16899C7.05208 1.38983 6.10833 1.00024 5 1.00024C3.89167 1.00024 2.94792 1.38983 2.16875 2.16899C1.38958 2.94816 1 3.89191 1 5.00024C1 6.10858 1.38958 7.05233 2.16875 7.83149C2.94792 8.61066 3.89167 9.00024 5 9.00024Z"
                          fill="#8F8F8F"
                        ></path>
                      </svg>
                      {formatTime(appointmentDetails?.buyerNote?.createdAt)}
                    </sapn>
                  </p>
                  <p className="text-sm text-black-secondary mt-2 mb-3 break-all">
                    {appointmentDetails?.buyerNote?.content}
                  </p>
                </div>
                {appointmentDetails?.sellerNote && (
                  <>
                    {!isEditMode ? (
                      <div className="grid place-items-end">
                        <p className="font-normal text-xs  text-gray-middle flex gap-3">
                          SFO Notes -{' '}
                          <span className="flex items-center gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="11"
                              viewBox="0 0 10 11"
                              fill="none"
                            >
                              <path
                                d="M7.5 1.49976H9.5C9.77615 1.49976 10 1.72362 10 1.99976V9.99976C10 10.2759 9.77615 10.4998 9.5 10.4998H0.5C0.22386 10.4998 0 10.2759 0 9.99976V1.99976C0 1.72362 0.22386 1.49976 0.5 1.49976H2.5V0.499756H3.5V1.49976H6.5V0.499756H7.5V1.49976ZM1 4.49976V9.49976H9V4.49976H1ZM2 5.49976H3V6.49976H2V5.49976ZM4.5 5.49976H5.5V6.49976H4.5V5.49976ZM7 5.49976H8V6.49976H7V5.49976Z"
                                fill="#8F8F8F"
                              ></path>
                            </svg>
                            {formatDate(
                              appointmentDetails?.sellerNote?.updatedAt
                            )}
                          </span>{' '}
                          <span className="flex items-center gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                            >
                              <path
                                d="M6.65 7.35024L7.35 6.65024L5.5 4.80024V2.50024H4.5V5.20024L6.65 7.35024ZM5 10.0002C4.30833 10.0002 3.65833 9.86899 3.05 9.60649C2.44167 9.34399 1.9125 8.98774 1.4625 8.53774C1.0125 8.08774 0.65625 7.55858 0.39375 6.95024C0.13125 6.34191 0 5.69191 0 5.00024C0 4.30858 0.13125 3.65858 0.39375 3.05024C0.65625 2.44191 1.0125 1.91274 1.4625 1.46274C1.9125 1.01274 2.44167 0.656494 3.05 0.393994C3.65833 0.131494 4.30833 0.000244141 5 0.000244141C5.69167 0.000244141 6.34167 0.131494 6.95 0.393994C7.55833 0.656494 8.0875 1.01274 8.5375 1.46274C8.9875 1.91274 9.34375 2.44191 9.60625 3.05024C9.86875 3.65858 10 4.30858 10 5.00024C10 5.69191 9.86875 6.34191 9.60625 6.95024C9.34375 7.55858 8.9875 8.08774 8.5375 8.53774C8.0875 8.98774 7.55833 9.34399 6.95 9.60649C6.34167 9.86899 5.69167 10.0002 5 10.0002ZM5 9.00024C6.10833 9.00024 7.05208 8.61066 7.83125 7.83149C8.61042 7.05233 9 6.10858 9 5.00024C9 3.89191 8.61042 2.94816 7.83125 2.16899C7.05208 1.38983 6.10833 1.00024 5 1.00024C3.89167 1.00024 2.94792 1.38983 2.16875 2.16899C1.38958 2.94816 1 3.89191 1 5.00024C1 6.10858 1.38958 7.05233 2.16875 7.83149C2.94792 8.61066 3.89167 9.00024 5 9.00024Z"
                                fill="#8F8F8F"
                              ></path>
                            </svg>
                            {formatTime(
                              appointmentDetails?.sellerNote?.updatedAt
                            )}
                          </span>
                        </p>
                        <div className="flex items-center gap-4">
                          <p className="text-sm text-black-secondary mt-2 break-all">
                            {appointmentDetails?.sellerNote?.content}
                          </p>
                          <span onClick={() => setIsEditMode(!isEditMode)}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="21"
                              viewBox="0 0 20 21"
                              fill="none"
                            >
                              <g opacity="0.5">
                                <path
                                  d="M15 9.25V16.4375C15 16.6427 14.9596 16.8459 14.8811 17.0354C14.8025 17.225 14.6874 17.3973 14.5424 17.5424C14.3973 17.6874 14.225 17.8025 14.0354 17.8811C13.8459 17.9596 13.6427 18 13.4375 18H4.0625C3.6481 18 3.25067 17.8354 2.95765 17.5424C2.66462 17.2493 2.5 16.8519 2.5 16.4375V7.0625C2.5 6.6481 2.66462 6.25067 2.95765 5.95765C3.25067 5.66462 3.6481 5.5 4.0625 5.5H10.6047"
                                  stroke="#585858"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M15.5286 3.94486L15.5287 3.94483C15.6133 3.86042 15.728 3.81302 15.8475 3.81302C15.9671 3.81302 16.0817 3.86042 16.1664 3.94483L16.1666 3.9451L16.5553 4.33573L16.5555 4.33593C16.6392 4.42047 16.6861 4.5346 16.6861 4.65353C16.6861 4.77246 16.6392 4.88658 16.5555 4.97113L16.5552 4.97144L9.51622 12.0237L15.5286 3.94486ZM15.5286 3.94486L8.47714 10.9838M15.5286 3.94486L8.03644 12.1479M8.03644 12.1479L8.0365 12.1479L8.36195 11.1785C8.3832 11.1049 8.42283 11.0379 8.47714 10.9838M8.03644 12.1479L8.03542 12.1513M8.03644 12.1479L8.03542 12.1513M8.47714 10.9838L8.5477 11.0547M8.47714 10.9838L8.47706 10.9839L8.5477 11.0547M8.5477 11.0547C8.50495 11.0973 8.47387 11.1501 8.45747 11.2082L8.5477 11.0547ZM8.03542 12.1513C8.02249 12.1949 8.02158 12.2412 8.03277 12.2853C8.04396 12.3294 8.06685 12.3696 8.09902 12.4018M8.03542 12.1513L8.09902 12.4018M8.34958 12.4655L8.34956 12.4654C8.30595 12.4783 8.25965 12.4793 8.21555 12.4681C8.17145 12.4569 8.13119 12.434 8.09902 12.4018M8.34958 12.4655L8.09902 12.4018C8.09902 12.4018 8.09902 12.4018 8.09902 12.4018M8.34958 12.4655L8.35299 12.4643M8.34958 12.4655L8.35299 12.4643M8.35299 12.4643L9.32152 12.1389M8.35299 12.4643L9.32152 12.1389M9.32152 12.1389C9.39509 12.1177 9.46207 12.078 9.51614 12.0238L9.32152 12.1389ZM16.5057 2.96819L16.5055 2.96839C16.4282 3.04574 16.3848 3.15062 16.3848 3.25997C16.3848 3.36933 16.4282 3.47421 16.5055 3.55156L16.5056 3.55164L16.9483 3.9936L17.0192 3.92306L16.9485 3.99383L16.9485 3.99375C16.9868 4.03219 17.0323 4.0627 17.0824 4.08352L17.1207 3.99118L17.0824 4.08352C17.1325 4.10437 17.1863 4.1151 17.2407 4.1151C17.295 4.1151 17.3488 4.10437 17.399 4.08352C17.4491 4.0627 17.4946 4.0322 17.5329 3.99377C17.5329 3.99371 17.533 3.99366 17.533 3.9936L18.0038 3.5252L18.0039 3.52508C18.2772 3.25219 18.3063 2.80394 18.0404 2.51278C17.9742 2.44007 17.894 2.38152 17.8046 2.34069C17.7151 2.29986 17.6183 2.27758 17.52 2.27521C17.4217 2.27284 17.324 2.29042 17.2327 2.3269C17.1414 2.36337 17.0584 2.41798 16.9888 2.48742L16.5057 2.96819Z"
                                  fill="#585858"
                                  stroke="#585858"
                                  strokeWidth="0.2"
                                />
                              </g>
                            </svg>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {/* <input className="w-full text-xs font-medium rounded border border-gray-mercury console-input p-2 my-3" /> */}
                        <UpdateAppointmentNote
                          setIsEditMode={setIsEditMode}
                          appointmentDetails={appointmentDetails}
                          handleUpdateAppointmentNote={
                            handleUpdateAppointmentNote
                          }
                          setAppointmentDetails={setAppointmentDetails}
                        />
                      </div>
                    )}
                  </>
                )}

                {!appointmentDetails?.sellerNote && (
                  <CreateAppointmentNote
                    appointmentDetails={appointmentDetails}
                    handleCreateAppointmentNote={handleCreateAppointmentNote}
                    setAppointmentDetails={setAppointmentDetails}
                  />
                )}
              </div>
            )}
          </section>
          <Modal
            isOpen={isOpen}
            onRequestClose={closeModal}
            contentLabel="Example Modal"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 31,
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className=" font-semibold md:text-2xl text-base">
                Cancel Appointment
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={closeModal}
              >
                &#x2715;
              </span>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid w-full relative">
                <textarea
                  rows="4"
                  placeholder="Add reason"
                  className="w-full text-xs font-medium rounded border border-purple-bdr console-input p-2"
                  {...register('comment')}
                  onInput={(event) => {
                    const maxLength = 200;
                    const value = event.target.value;
                    if (value.length > maxLength) {
                      event.target.value = value.slice(0, maxLength);
                    }
                  }}
                />
                {errors && errors?.comment && (
                  <p className="text-red-drk md:text-r0.6875 text-r0.625 top-20 absolute">
                    {errors?.comment?.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2  gap-2  md:gap-8 mt-6">
                <button
                  onClick={closeModal}
                  type="button"
                  className="flex-1 md:flex-none h-10 w-full  border border-gray-dark bg-very-light text-gray-dark text-xs hover:text-gray-middle hover:border-very-dark py-2 text-center font-bold rounded-md"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="flex-1 md:flex-none h-10 w-full border bg-red-cover text-white-main  text-xs hover:bg-red-danger-hover py-2 text-center font-bold rounded-md"
                >
                  Cancel
                </button>
              </div>
            </form>
          </Modal>
          <Modal
            isOpen={RescheduleModal}
            onRequestClose={closeRescheduleModal}
            contentLabel="Example Modal"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 31,
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',

                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className=" font-semibold md:text-2xl text-base">
                Reschedule Appointment
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={closeRescheduleModal}
              >
                &#x2715;
              </span>
            </div>
            <RescheduleAppointmentForm
              selectedAppointment={selectedAppointment}
              closeRescheduleModal={closeRescheduleModal}
              details={true}
              setAppointmentDetails={setAppointmentDetails}
            />
          </Modal>
        </section>
      </section>
    </div>
  );
};

export default AppointmentDetails;
